import { useTranslations } from 'next-intl'

import { calculateAddonCost } from '@/open-web/components/UpgradeFlow/utils'
import { getPriceBaseOnCustomerType } from '@/open-web/utils/getPriceBaseOnCustomerType'
import { discountPercentToString, getPriceDetails } from '@/shared/components/Price/utils'
import type { PriceItemProps } from '@/shared/components/PriceItem'
import type { CustomerType } from '@/shared/graphql/schema/commonBackend/graphql'
import { usePriceToString } from '@/shared/hooks/usePriceToStringFormatter'
import type { EnrichedAvailableAddon } from '@/shared/services/campaignDataResolver'
import { logError } from '@/shared/utils/error'
import { UiError } from '@/shared/utils/errorClasses'

export type AddonPriceSectionProps = {
  addon?: EnrichedAvailableAddon
  customerType?: CustomerType
  showLabel?: boolean
  priceItems?: PriceItemProps[]
}

export type AddonPriceInfoTemplateProps = Omit<AddonPriceSectionProps, 'priceItems'>

export const useAddonPrice = ({
  addon,
  customerType,
  showLabel,
  priceItems: propsPriceItems,
}: {
  addon?: EnrichedAvailableAddon
  customerType?: CustomerType
  showLabel?: boolean
  priceItems?: PriceItemProps[]
}) => {
  const t = useTranslations('purchaseFlow')
  const { formatPrice, formatPriceWithUnit } = usePriceToString()

  const getInlinePriceString = (
    energy: number | null | undefined,
    monthlyFee: number | null | undefined,
    energyUnit: string | null | undefined,
    monthlyFeeUnit: string | null | undefined,
  ) => {
    const formattedEnergy = formatPriceWithUnit(energy, energyUnit)
    const formattedMonthlyFee = formatPriceWithUnit(monthlyFee, monthlyFeeUnit)
    const hasEnergyPrice = typeof energy === 'number' && energyUnit
    const hasMonthlyFeePrice = typeof monthlyFee === 'number' && monthlyFeeUnit

    if (hasEnergyPrice && hasMonthlyFeePrice) {
      return `${formattedMonthlyFee} + ${formattedEnergy}`
    }

    if (hasMonthlyFeePrice) {
      return formattedMonthlyFee
    }

    if (hasEnergyPrice) {
      return formattedEnergy
    }

    logError(
      new UiError(`Cannot show price for an addon ${addon?.productName}`, 'AddonPriceSection'),
    )

    return 'unknown'
  }

  const getAddonPriceInfoTemplate = ({
    addon,
    customerType,
    showLabel,
  }: AddonPriceInfoTemplateProps): PriceItemProps[] => {
    if (!addon || !customerType) {
      return []
    }

    const {
      energy,
      energyWithDiscount,
      monthlyFee,
      monthlyFeeWithDiscount,
      energyDiscountDuration,
      monthlyFeeDiscountDuration,
    } = calculateAddonCost(addon)

    const monthlyFeePrice = getPriceBaseOnCustomerType(monthlyFee, customerType)
    const monthlyFeePriceWithDiscount = getPriceBaseOnCustomerType(
      monthlyFeeWithDiscount,
      customerType,
    )
    const monthlyFeeUnit = monthlyFee?.priceUnit

    const energyPrice = getPriceBaseOnCustomerType(energy, customerType)
    const energyPriceWithDiscount = getPriceBaseOnCustomerType(energyWithDiscount, customerType)
    const energyUnit = energy?.priceUnit

    const isMonthlyFeeDiscounted =
      monthlyFeePrice !== null &&
      monthlyFeePriceWithDiscount !== null &&
      monthlyFeePriceWithDiscount < monthlyFeePrice

    const isEnergyDiscounted =
      energyPrice !== null &&
      energyPriceWithDiscount !== null &&
      energyPriceWithDiscount < energyPrice

    const { priceWithoutDiscount, priceWithDiscount } = getPriceDetails({
      isMonthlyFeeDiscounted,
      isEnergyDiscounted,
      monthlyFee,
      monthlyFeeWithDiscount,
      energy,
      energyWithDiscount,
      customerType,
    })

    const priceString = getInlinePriceString(
      energyPriceWithDiscount,
      monthlyFeePriceWithDiscount,
      energyUnit,
      monthlyFeeUnit,
    )
    const originalPriceString = getInlinePriceString(
      isEnergyDiscounted ? energyPrice : null,
      monthlyFeePrice,
      energyUnit,
      monthlyFeeUnit,
    )

    const discountDuration =
      monthlyFeeDiscountDuration?.amount || energyDiscountDuration?.amount || 'none'

    const getDiscountType = (isMonthlyFeeDiscounted: boolean, isEnergyDiscounted: boolean) => {
      if (isMonthlyFeeDiscounted && isEnergyDiscounted) {
        return 'energyAndMonthlyFee'
      }
      if (isMonthlyFeeDiscounted) {
        return 'monthlyFee'
      }
      if (isEnergyDiscounted) {
        return 'energy'
      }
      return
    }

    const discountType = getDiscountType(isMonthlyFeeDiscounted, isEnergyDiscounted)

    return [
      {
        priceLabel: showLabel ? addon.cfData.title : undefined,
        price: priceString,
        originalPrice:
          isMonthlyFeeDiscounted || isEnergyDiscounted ? originalPriceString : undefined,
        originalPriceDescription: discountType
          ? t('discount', {
              discountDuration,
              discountDurationType: 'MONTH',
              discountType,
              discountPercent: discountPercentToString(
                priceWithoutDiscount,
                priceWithDiscount,
                formatPrice,
              ),
            })
          : undefined,
      },
    ]
  }

  const priceItems =
    propsPriceItems || getAddonPriceInfoTemplate({ addon, customerType, showLabel })

  return {
    priceItems,
    formatPrice,
    formatPriceWithUnit,
    getInlinePriceString,
  }
}
